import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { ApiError, auth, type Account } from '../api'

type Status = 'loading' | 'signedOut' | 'signedIn'

interface AuthState {
  status: Status
  account: Account | null
  login: (email: string, password: string) => Promise<Account>
  /**
   * The API answers 201 for new and existing emails alike (no enumeration), so the
   * result is read from /auth/me: the account when the cookie is a real session,
   * null when it is not (the user must check their email or log in).
   */
  signup: (input: { email: string; password: string; accept_terms: true; name?: string; locale?: 'sq' | 'en'; turnstile_token?: string }) => Promise<Account | null>
  logout: () => Promise<void>
  /** Re-reads /auth/me (balance, verification). */
  refresh: () => Promise<void>
  setBalance: (microEur: number) => void
}

const AuthContext = createContext<AuthState | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [account, setAccount] = useState<Account | null>(null)
  const [status, setStatus] = useState<Status>('loading')

  const refresh = useCallback(async () => {
    try {
      setAccount(await auth.me())
      setStatus('signedIn')
    } catch (error) {
      if (error instanceof ApiError && error.status !== 401 && error.status !== 0) throw error
      setAccount(null)
      setStatus('signedOut')
    }
  }, [])

  useEffect(() => {
    void refresh().catch(() => {
      setAccount(null)
      setStatus('signedOut')
    })
  }, [refresh])

  const value = useMemo<AuthState>(
    () => ({
      status,
      account,
      refresh,
      login: async (email, password) => {
        const acct = await auth.login({ email, password })
        setAccount(acct)
        setStatus('signedIn')
        return acct
      },
      signup: async (input) => {
        await auth.signup(input)
        try {
          const acct = await auth.me()
          setAccount(acct)
          setStatus('signedIn')
          return acct
        } catch (error) {
          if (error instanceof ApiError && error.status === 401) return null
          throw error
        }
      },
      logout: async () => {
        try {
          await auth.logout()
        } finally {
          setAccount(null)
          setStatus('signedOut')
        }
      },
      setBalance: (micro) => setAccount((a) => (a ? { ...a, balance_micro_eur: micro } : a)),
    }),
    [status, account, refresh],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider')
  return ctx
}

/** The auth state, or null outside an AuthProvider (a few editor pieces also render in isolation). */
export function useAuthOptional(): AuthState | null {
  return useContext(AuthContext)
}
